// src/components/customer/ProductCarouselSection.tsx

import React, { useRef, useState, useEffect } from "react";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Star,
} from "lucide-react";
import { ImagePath } from "../../assests";
import CommonButton from "../CommonButton";
import CartButton from "./CartButton";
import CountdownTimer from "./ProductDetailComponent.tsx/CountdownTimer";
import { ProductItem } from "../../utils/Customer/CustomerTypes";
import {
  useCreateCartMutation,
  useLazyGetCartQuery,
  useUpdateCartMutation,
} from "../../redux/actions/Customer/cartCustomer";
import CartDrawer, { CartItem } from "./ProductDetailComponent.tsx/CartDrawer";
import { useAlert } from "./AlertContext";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/rootReducer";

interface ProductCarouselSectionProp {
  showLogo?: boolean;
  title?: React.ReactNode;
  highlightText?: string;
  highlightClassName?: string;
  handleProductPage?: (product: ProductItem) => void;
  products: any[];
  headingPosition?: "left" | "center" | "right";
  onAddToCartClick?: (product: ProductItem) => void;
  handleViewAllClick?: () => void;
  handleCheckoutClick?: () => void;
}

const ProductCarouselSection: React.FC<ProductCarouselSectionProp> = ({
  showLogo = true,
  title = "Essentials for Happy Paws",
  handleProductPage,
  products = [],
  headingPosition = "center",
  onAddToCartClick,
  handleViewAllClick,
  handleCheckoutClick,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const [activeIndex, setActiveIndex] = useState<{
    [key: number]: number;
  }>({});

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const [timers, setTimers] = useState<{
    [key: number]: number;
  }>({});

  const [activeCartIndex, setActiveCartIndex] = useState<number | null>(null);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const { showConfirm } = useAlert();

  // API Mutations & Queries
  const [createCart] = useCreateCartMutation();
  const [updateCart] = useUpdateCartMutation();
  const [getCartTrigger, { data: cartData }] = useLazyGetCartQuery();

  const isLoggedIn = useSelector(
    (state: RootState) => state.auth.customer.isLoggedIn,
  );

  // ============================================================
  // IMAGE HELPERS
  // ============================================================

  const getProductImages = (product: any): string[] => {
    const mainImage =
      product?.ImageUrl || product?.imageUrl || product?.productimage || "";

    const rawImages =
      product?.Images ||
      product?.images ||
      product?.subImages ||
      product?.subimage ||
      [];

    const parsedImages = Array.isArray(rawImages)
      ? rawImages
          .map((image: any) => {
            if (typeof image === "string") {
              return image;
            }

            return image?.image || image?.imageUrl || image?.ImageUrl || "";
          })
          .filter(Boolean)
      : [];

    /*
     * IMPORTANT:
     *
     * API Images already contains the main image:
     *
     * Images: [
     *   "image1.jpg",
     *   "image2.jpg",
     *   ...
     * ]
     *
     * Therefore we DO NOT do:
     *
     * [mainImage, ...parsedImages]
     *
     * because that would create:
     *
     * image1, image1, image2...
     */

    if (parsedImages.length > 0) {
      return parsedImages;
    }

    // Fallback when Images is empty
    return mainImage ? [mainImage] : [];
  };

  // ============================================================
  // AUTO SLIDESHOW ON HOVER
  // ============================================================

  useEffect(() => {
    if (hoveredIndex === null) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => {
        const updated = { ...prev };

        const product = products[hoveredIndex];

        if (!product) {
          return prev;
        }

        const images = getProductImages(product);

        if (images.length <= 1) {
          return prev;
        }

        const current = updated[hoveredIndex] ?? 0;

        updated[hoveredIndex] = (current + 1) % images.length;

        return updated;
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [hoveredIndex, products]);

  // ============================================================
  // SALE TIMER
  // ============================================================

  const getRemainingSeconds = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return 0;

    try {
      const now = new Date();

      const [startDay, startMonth, startYear] = startDate
        .split("-")
        .map(Number);

      const [endDay, endMonth, endYear] = endDate.split("-").map(Number);

      const start = new Date(startYear, startMonth - 1, startDay);

      const end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59);

      if (now < start) return 0;

      return Math.max(0, Math.floor((end.getTime() - now.getTime()) / 1000));
    } catch (e) {
      return 0;
    }
  };

  useEffect(() => {
    const initialTimers: Record<number, number> = {};

    products.forEach((product, index) => {
      initialTimers[index] = product.RemainingSaleSeconds ?? 0;
    });

    setTimers(initialTimers);
  }, [products]);

  // ============================================================
  // SCROLL
  // ============================================================

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -320 : 320,
        behavior: "smooth",
      });
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

      setCanScrollLeft(scrollLeft > 0);

      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  // ============================================================
  // CART
  // ============================================================

  const syncCartWithServer = (serverItems: any[]) => {
    const mappedItems: CartItem[] = serverItems.map((item) => ({
      id: item.cartItemId.toString(),
      productId: item.productId,
      title: item.productName,
      price: `AED ${item.unitSalePrice || item.unitPrice}`,
      image: item.imageUrl,
      quantity: item.quantity,
      isMandatory: item.isMandatory,
    }));

    setCart(mappedItems);
  };

  const fetchLatestCart = async () => {
    try {
      const res = await getCartTrigger().unwrap();

      const items = res?.data?.items || [];

      syncCartWithServer(items);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAdd = async (product: ProductItem, index: number) => {
    try {
      await createCart({
        productId: Number(product.ProductId),
        quantity: 1,
      }).unwrap();

      await fetchLatestCart();

      setActiveCartIndex(index);
      setIsOpen(true);

      onAddToCartClick?.(product);
    } catch (error: any) {
      if (isLoggedIn) {
        showConfirm({
          title: "Product Error",
          message: error?.data?.Message || "Failed to Add Product",
          type: "error",
          confirmText: "Ok",
        });
      }
    }
  };

  const handleIncrement = async (itemId: string) => {
    const targetItem =
      cartData?.data?.items?.find(
        (item: any) => item.cartItemId.toString() === itemId.toString(),
      ) ||
      cartData?.data?.items?.find(
        (item: any) => item.cartItemId.toString() === itemId.toString(),
      );

    if (!targetItem) return;

    try {
      await updateCart({
        cartItemId: Number(itemId),
        quantity: targetItem.quantity + 1,
      }).unwrap();

      await fetchLatestCart();
    } catch (error: any) {
      showConfirm({
        message: error?.data?.Message || "Failed to Add Product",
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleDecrement = async (itemId: string) => {
    const targetItem =
      cartData?.data?.items?.find(
        (item: any) => item.cartItemId.toString() === itemId.toString(),
      ) ||
      cartData?.data?.items?.find(
        (item: any) => item.cartItemId.toString() === itemId.toString(),
      );

    if (!targetItem) return;

    try {
      if (targetItem.quantity <= 1) {
        await handleRemoveProduct(itemId);
      } else {
        await updateCart({
          cartItemId: Number(itemId),
          quantity: targetItem.quantity - 1,
        }).unwrap();

        await fetchLatestCart();
      }
    } catch (error: any) {
      showConfirm({
        title: "Cart",
        message: error?.data?.Message || "Failed to Add Product",
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleRemoveProduct = async (itemId: string) => {
    try {
      await updateCart({
        cartItemId: Number(itemId),
        quantity: 0,
      }).unwrap();

      await fetchLatestCart();
    } catch (error: any) {
      showConfirm({
        title: "Cart",
        message: error?.data?.Message || "Failed to Add Product",
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleAddToCartFromSlider = async (sliderProduct: any) => {
    try {
      await createCart({
        productId: Number(
          sliderProduct.productId ||
            sliderProduct.ProductId ||
            sliderProduct.id,
        ),
        quantity: 1,
      }).unwrap();

      await fetchLatestCart();

      setIsOpen(true);
    } catch (error: any) {
      showConfirm({
        title: "Cart",
        message: error?.data?.Message || "Failed to Add Product",
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <section className="relative overflow-hidden w-full">
      <div className="relative z-10">
        {/* SECTION HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between relative gap-3 pb-3">
          {showLogo && ImagePath.JoseraLogo && (
            <img
              src={ImagePath.JoseraLogo}
              alt="Brand Logo"
              className="h-10 object-contain self-start md:self-auto"
            />
          )}

          <h2
            className={`text-xl md:text-2xl font-bold text-iconColor flex-1 ${
              headingPosition === "left"
                ? "text-left"
                : headingPosition === "right"
                  ? "text-right"
                  : "text-center"
            }`}
          >
            {title}
          </h2>

          <div className="hidden md:flex ml-auto">
            <CommonButton
              text="View All"
              icon={<ChevronRight size={16} />}
              iconPosition="right"
              onClick={handleViewAllClick}
            />
          </div>
        </div>

        {/* CAROUSEL */}
        <div className="relative">
          {/* LEFT ARROW */}
          <div className="absolute left-1 top-1/2 -translate-y-1/2 z-40">
            <CommonButton
              icon={<ChevronLeft size={25} />}
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className="bg-white shadow-md rounded-full hover:bg-gray-50 transition disabled:opacity-30"
            />
          </div>

          {/* RIGHT ARROW */}
          <div className="absolute right-1 top-1/2 -translate-y-1/2 z-40">
            <CommonButton
              icon={<ChevronRight size={25} />}
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className="bg-white shadow-md rounded-full hover:bg-gray-50 transition disabled:opacity-30"
            />
          </div>

          {/* HORIZONTAL SCROLLER */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex gap-6 overflow-x-auto [&::-webkit-scrollbar]:hidden pt-2 pb-4 scroll-smooth"
          >
            {products.map((product, index) => {
              const pId = product.ProductId || product.productId || product.id;

              const isOutOfStock =
                product.InStock === false ||
                product.inStock === false ||
                product.IsStock === false ||
                product.isStock === false;

              const productName =
                product.Name || product.productname || "Premium Pet Item";

              // ==================================================
              // PRODUCT IMAGES
              // ==================================================

              const images = getProductImages(product);

              const hasMultipleImages = images.length > 1;

              const currentIndex = activeIndex[index] ?? 0;

              const currentImage =
                images[currentIndex] || "https://via.placeholder.com/300";

              // ==================================================
              // PRICE
              // ==================================================

              const originalPrice = Number(
                product.price ?? product.Price ?? product.originalPrice ?? 0,
              );

              const currentSalePrice = Number(
                product.salePrice ?? product.saleprice ?? 0,
              );

              const hasSalePrice = currentSalePrice > 0;

              const hasDiscount =
                hasSalePrice && originalPrice > currentSalePrice;

              const discountPercentage = hasDiscount
                ? Math.round(
                    ((originalPrice - currentSalePrice) / originalPrice) * 100,
                  )
                : 0;

              // ==================================================
              // MARKETING TEXT
              // ==================================================

              const loopFeatures = product.MarketingTexts || [];

              // ==================================================
              // CART
              // ==================================================

              const itemsList =
                cartData?.data?.items || cartData?.data.items || [];

              const cartMatch = itemsList.find(
                (item: any) => Number(item.productId) === Number(pId),
              );

              const liveQty = cartMatch ? cartMatch.quantity : 0;

              const cartItemId = cartMatch ? cartMatch.cartItemId : "";

              // ==================================================
              // CARD
              // ==================================================

              return (
                <div
                  key={pId || index}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => handleProductPage?.(product)}
                  className={`group w-[220px] md:w-[240px] lg:w-[260px] flex-shrink-0 rounded-2xl flex flex-col justify-between border border-gray-200 bg-white transition-all duration-300 hover:-translate-y-2 hover:shadow-xl cursor-pointer ${
                    isOutOfStock ? "opacity-85" : ""
                  }`}
                >
                  {/* ==========================================
                      IMAGE AREA
                  =========================================== */}

                  <div className="relative h-[245px] md:h-[285px] rounded-t-2xl flex items-center justify-center overflow-hidden bg-gray-50 group">
                    <img
                      src={currentImage}
                      alt={productName}
                      className={`h-full w-full object-contain transition-all duration-500 transform group-hover:scale-105 ${
                        isOutOfStock ? "grayscale-[35%]" : ""
                      }`}
                    />

                    {/* OUT OF STOCK */}
                    {isOutOfStock && (
                      <div className="absolute inset-0 bg-black/25 flex items-center justify-center z-20 pointer-events-none">
                        <span className="bg-red-600 text-white font-semibold text-xs md:text-sm px-3 py-1 rounded-full shadow-md uppercase tracking-wider">
                          Sold Out
                        </span>
                      </div>
                    )}

                    {/* RATING */}
                    {product.Rating != null && Number(product.Rating) > 0 && (
                      <div className="absolute bottom-3 left-0 z-20 bg-blue-800/90 backdrop-blur px-2 py-1 rounded-r-lg flex items-center gap-1 shadow-sm">
                        <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />

                        <span className="text-xs font-semibold text-white">
                          {product.Rating}
                        </span>
                      </div>
                    )}

                    {/* PREVIOUS IMAGE */}
                    {hasMultipleImages && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();

                          setActiveIndex((prev) => {
                            const current = prev[index] ?? 0;

                            const newIndex =
                              current === 0 ? images.length - 1 : current - 1;

                            return {
                              ...prev,
                              [index]: newIndex,
                            };
                          });
                        }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 p-1.5 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition duration-200 hover:bg-white"
                      >
                        <ChevronLeft size={16} className="text-gray-700" />
                      </button>
                    )}

                    {/* NEXT IMAGE */}
                    {hasMultipleImages && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();

                          setActiveIndex((prev) => {
                            const current = prev[index] ?? 0;

                            const newIndex = (current + 1) % images.length;

                            return {
                              ...prev,
                              [index]: newIndex,
                            };
                          });
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 p-1.5 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition duration-200 hover:bg-white"
                      >
                        <ChevronRight size={16} className="text-gray-700" />
                      </button>
                    )}

                    {/* IMAGE INDICATORS */}
                    {hasMultipleImages && activeCartIndex !== index && (
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-20 bg-white/80 px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition duration-300">
                        {images.map((_, i) => (
                          <div
                            key={i}
                            className={`h-1 rounded-full transition-all ${
                              i === currentIndex
                                ? "w-3 bg-black"
                                : "w-1 bg-black/30"
                            }`}
                          />
                        ))}
                      </div>
                    )}

                    {/* COUNTDOWN */}
                    {timers[index] > 0 && !isOutOfStock && (
                      <CountdownTimer
                        totalSeconds={timers[index]}
                        className="absolute top-3 right-3 shadow-sm"
                      />
                    )}
                  </div>

                  {/* ==========================================
                      DETAILS
                  =========================================== */}

                  <div className="p-3 flex flex-col flex-1 justify-between">
                    <div>
                      {/* PRODUCT NAME */}
                      <p className="text-sm md:text-base text-logoHeading font-semibold line-clamp-2 min-h-[2.5rem] leading-tight mb-1">
                        {productName}
                      </p>

                      {/* MARKETING TEXT */}
                      {loopFeatures && loopFeatures.length > 0 && (
                        <div className="relative h-[18px] overflow-hidden mt-2 mb-2">
                          <div className="animate-vertical-scroll">
                            {loopFeatures.map((text: string, i: number) => (
                              <div
                                key={i}
                                className="flex items-center gap-1.5 h-[18px]"
                              >
                                <Sparkles
                                  size={12}
                                  className="text-[#2F5E2E] flex-shrink-0"
                                />

                                <p className="text-xs font-semibold text-gray-700 truncate leading-none">
                                  {text}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* PRICE / CART */}
                    {product.ItemType !== "Custom" && (
                      <div>
                        <div className="flex items-end justify-between mt-2 pt-1 border-t border-gray-50">
                          <div className="flex flex-col leading-tight">
                            {hasDiscount ? (
                              <>
                                {/* Original Price */}
                                <span className="text-xs text-gray-400 line-through">
                                  AED {originalPrice.toFixed(2)}
                                </span>

                                {/* Sale Price + Discount */}
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-xl font-bold text-logoHeading">
                                    AED {currentSalePrice.toFixed(2)}
                                  </span>

                                  {discountPercentage > 0 && (
                                    <span
                                      className="
              inline-flex
              items-center
              rounded-md
              bg-rose-50
              px-1.5
              py-0.5
              text-[9px]
              font-bold
              text-rose-600
              whitespace-nowrap
            "
                                    >
                                      {discountPercentage}% OFF
                                    </span>
                                  )}
                                </div>
                              </>
                            ) : (
                              <span className="text-xl font-bold text-logoHeading">
                                AED {originalPrice.toFixed(2)}
                              </span>
                            )}
                          </div>

                          {/* CART BUTTON */}
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className="relative z-30"
                          >
                            {isOutOfStock ? (
                              <span className="inline-block text-xs font-semibold text-red-600 bg-red-50 border border-red-200 px-2.5 py-1.5 rounded-lg select-none">
                                Sold Out
                              </span>
                            ) : (
                              <CartButton
                                qty={liveQty}
                                onAdd={() => handleAdd(product, index)}
                                onIncrement={() =>
                                  handleIncrement(cartItemId.toString())
                                }
                                onDecrement={() =>
                                  handleDecrement(cartItemId.toString())
                                }
                              />
                            )}
                          </div>
                        </div>

                        {/* ADDED TO CART */}
                        {activeCartIndex === index && !isOutOfStock && (
                          <div className="mt-2 text-center animate-toast">
                            <div className="flex items-center justify-center gap-1.5 text-green-600 text-xs font-medium bg-green-50 py-1 rounded-md border border-green-100">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Added to Cart
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* MOBILE VIEW ALL */}
          <div className="flex items-center justify-center md:hidden pt-3">
            <CommonButton
              text="View All Products"
              icon={<ChevronRight size={16} />}
              iconPosition="right"
              onClick={handleViewAllClick}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* CART DRAWER */}
      <CartDrawer
        cart={cart}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onIncrement={handleIncrement}
        onDecrement={handleDecrement}
        onRemove={handleRemoveProduct}
        onAddToCart={handleAddToCartFromSlider}
        handleCheckoutClick={handleCheckoutClick!}
      />
    </section>
  );
};

export default ProductCarouselSection;
